import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Card,
  CardContent,
  Tooltip
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { KennzeichenService } from '../services/kennzeichenService';
import { KennzeichensucheRequest, KennzeichensucheResponse } from '../types/kennzeichen';

const SORTIERUNG_OPTIONS = [
  { value: 'aufsteigend', label: 'Aufsteigend' },
  { value: 'absteigend', label: 'Absteigend' },
  { value: 'zufaellig', label: 'Zufällig' }
];

const KennzeichenSuchePage: React.FC = () => {
  const [formData, setFormData] = useState<KennzeichensucheRequest>({
    unterscheidungszeichen: '',
    erkennungszeichen: '',
    erkennungsnummer: '',
    kennzeichenMuster: '',
    maxTreffer: 10,
    kundennummer: '',
    merkmalOldtimer: false,
    merkmalElektro: false,
    merkmalWechselkennzeichen: false,
    merkmalGruenesKennzeichen: false,
    laengeMinimal: undefined,
    laengeMaximal: undefined,
    saisonVon: undefined,
    saisonBis: undefined,
    sortierung: 'aufsteigend',
    zustaendigeBehoerde: ''
  });

  const [result, setResult] = useState<KennzeichensucheResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isKomplettSuche = !!formData.kennzeichenMuster?.trim();

  const handleChange = (field: keyof KennzeichensucheRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNumberChange = (field: 'maxTreffer' | 'laengeMinimal' | 'laengeMaximal' | 'saisonVon' | 'saisonBis', value: string) => {
    const num = value === '' ? undefined : parseInt(value, 10);
    handleChange(field, num);
  };

  const buildRequest = (): KennzeichensucheRequest => {
    const request: KennzeichensucheRequest = {
      unterscheidungszeichen: formData.unterscheidungszeichen?.trim() || '',
      maxTreffer: formData.maxTreffer ?? 10,
      sortierung: formData.sortierung || 'aufsteigend'
    };

    if (isKomplettSuche) {
      request.kennzeichenMuster = formData.kennzeichenMuster?.trim() || '';
      request.unterscheidungszeichen = formData.unterscheidungszeichen?.trim() || '';
    } else {
      if (formData.erkennungszeichen?.trim()) request.erkennungszeichen = formData.erkennungszeichen.trim();
      if (formData.erkennungsnummer?.trim()) request.erkennungsnummer = formData.erkennungsnummer.trim();
    }

    if (formData.kundennummer?.trim()) request.kundennummer = formData.kundennummer.trim();
    if (formData.zustaendigeBehoerde?.trim()) request.zustaendigeBehoerde = formData.zustaendigeBehoerde.trim();
    if (formData.merkmalOldtimer) request.merkmalOldtimer = true;
    if (formData.merkmalElektro) request.merkmalElektro = true;
    if (formData.merkmalWechselkennzeichen) request.merkmalWechselkennzeichen = true;
    if (formData.merkmalGruenesKennzeichen) request.merkmalGruenesKennzeichen = true;
    if (formData.laengeMinimal != null) request.laengeMinimal = formData.laengeMinimal;
    if (formData.laengeMaximal != null) request.laengeMaximal = formData.laengeMaximal;
    if (formData.saisonVon != null) request.saisonVon = formData.saisonVon;
    if (formData.saisonBis != null) request.saisonBis = formData.saisonBis;

    return request;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setResult(null);

    const request = buildRequest();

    if (!request.unterscheidungszeichen) {
      setError('Unterscheidungszeichen ist ein Pflichtfeld.');
      return;
    }

    if (!isKomplettSuche && !request.erkennungszeichen && !request.erkennungsnummer) {
      setError('Bitte Erkennungszeichen, Erkennungsnummer oder ein Kennzeichenmuster angeben.');
      return;
    }

    setLoading(true);
    try {
      const data = await KennzeichenService.sucheKennzeichen(request);
      setResult(data);

      if (!data.erfolg) {
        setError(data.fehlerMeldung || 'Fehler bei der Kennzeichensuche.');
      }
    } catch (err: any) {
      console.error('Fehler bei der Kennzeichensuche:', err);
      setError(err.response?.data?.fehlerMeldung || err.response?.data?.message || 'Fehler bei der Kennzeichensuche.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      unterscheidungszeichen: '',
      erkennungszeichen: '',
      erkennungsnummer: '',
      kennzeichenMuster: '',
      maxTreffer: 10,
      kundennummer: '',
      merkmalOldtimer: false,
      merkmalElektro: false,
      merkmalWechselkennzeichen: false,
      merkmalGruenesKennzeichen: false,
      laengeMinimal: undefined,
      laengeMaximal: undefined,
      saisonVon: undefined,
      saisonBis: undefined,
      sortierung: 'aufsteigend',
      zustaendigeBehoerde: ''
    });
    setResult(null);
    setError(null);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Kennzeichensuche
      </Typography>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Unterscheidungszeichen *"
                value={formData.unterscheidungszeichen}
                onChange={(e) => handleChange('unterscheidungszeichen', e.target.value)}
                placeholder="z.B. B, M, HH"
                helperText="Pflicht"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Erkennungszeichen"
                value={formData.erkennungszeichen}
                onChange={(e) => handleChange('erkennungszeichen', e.target.value)}
                placeholder="z.B. AB, A*, ?B"
                disabled={isKomplettSuche}
                helperText={isKomplettSuche ? 'Nur bei Einzelsuche' : ''}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Erkennungsnummer"
                value={formData.erkennungsnummer}
                onChange={(e) => handleChange('erkennungsnummer', e.target.value)}
                placeholder="z.B. 123, 1*, ?23"
                disabled={isKomplettSuche}
                helperText={isKomplettSuche ? 'Nur bei Einzelsuche' : ''}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                type="number"
                label="Max. Treffer"
                value={formData.maxTreffer ?? ''}
                onChange={(e) => handleNumberChange('maxTreffer', e.target.value)}
                inputProps={{ min: 1, max: 100 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Tooltip title="Bei Eingabe wird die Komplettsuche verwendet (Wildcards erlaubt)">
                <TextField
                  fullWidth
                  label="Kennzeichenmuster"
                  value={formData.kennzeichenMuster}
                  onChange={(e) => handleChange('kennzeichenMuster', e.target.value)}
                  placeholder="z.B. B-?? 1*"
                  helperText="Beispiel: B-AB 123, B-?? 1*"
                />
              </Tooltip>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Kundennummer"
                value={formData.kundennummer}
                onChange={(e) => handleChange('kundennummer', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Zuständige Behörde (KPS)"
                value={formData.zustaendigeBehoerde}
                onChange={(e) => handleChange('zustaendigeBehoerde', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.merkmalOldtimer}
                    onChange={(e) => handleChange('merkmalOldtimer', e.target.checked)}
                  />
                }
                label="H-Kennzeichen (Oldtimer)"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.merkmalElektro}
                    onChange={(e) => handleChange('merkmalElektro', e.target.checked)}
                  />
                }
                label="E-Kennzeichen (Elektro)"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.merkmalWechselkennzeichen}
                    onChange={(e) => handleChange('merkmalWechselkennzeichen', e.target.checked)}
                  />
                }
                label="Wechselkennzeichen"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.merkmalGruenesKennzeichen}
                    onChange={(e) => handleChange('merkmalGruenesKennzeichen', e.target.checked)}
                  />
                }
                label="Grünes Kennzeichen"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Minimale Länge"
                value={formData.laengeMinimal ?? ''}
                onChange={(e) => handleNumberChange('laengeMinimal', e.target.value)}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Maximale Länge"
                value={formData.laengeMaximal ?? ''}
                onChange={(e) => handleNumberChange('laengeMaximal', e.target.value)}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Saison von (Monat)"
                value={formData.saisonVon ?? ''}
                onChange={(e) => handleNumberChange('saisonVon', e.target.value)}
                inputProps={{ min: 1, max: 12 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Saison bis (Monat)"
                value={formData.saisonBis ?? ''}
                onChange={(e) => handleNumberChange('saisonBis', e.target.value)}
                inputProps={{ min: 1, max: 12 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Sortierung</InputLabel>
                <Select
                  value={formData.sortierung || 'aufsteigend'}
                  label="Sortierung"
                  onChange={(e) => handleChange('sortierung', e.target.value)}
                >
                  {SORTIERUNG_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box display="flex" gap={2} mt={3}>
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              disabled={loading}
            >
              Suchen
            </Button>
            <Button variant="outlined" onClick={handleReset} disabled={loading}>
              Zurücksetzen
            </Button>
          </Box>
        </form>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {result?.erfolg && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Ergebnisse ({result.anzahlTreffer} Treffer)
          </Typography>

          {result.nachrichtenId && (
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Nachrichten-ID: {result.nachrichtenId}
              {result.zeitstempel && ` | Zeitstempel: ${new Date(result.zeitstempel).toLocaleString('de-DE')}`}
            </Typography>
          )}

          <Box display="flex" flexWrap="wrap" gap={1} mt={2}>
            {result.kennzeichen.map((kz, index) => (
              <Chip key={index} label={kz} color="primary" variant="outlined" />
            ))}
          </Box>

          {result.kennzeichen.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Keine Kennzeichen gefunden.
            </Alert>
          )}

          {result.wechselkennzeichen && result.wechselkennzeichen.length > 0 && (
            <Box mt={3}>
              <Typography variant="subtitle1" gutterBottom>
                Wechselkennzeichen
              </Typography>
              <Grid container spacing={2}>
                {result.wechselkennzeichen.map((wk, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body1" fontWeight="bold">
                          {wk.gemeinsamerTeil}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {wk.fahrzeugspezifischerTeil1} / {wk.fahrzeugspezifischerTeil2}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default KennzeichenSuchePage;
